import type { EfsLang } from '@/lib/i18n';
import { DEFAULT_LANG } from '@/lib/i18n';
import type { CurrentUser } from '@/lib/permissions';
import { hasPermission } from '@/lib/permissions';

import type { I18nText, WorkPackageDto } from './types';

/**
 * True when the user holds workpackage.manage AND is a platform/system actor.
 * The backend CREATE/PATCH /work-packages gates on this exact combination.
 */
export const canAuthorWorkPackages = (user: CurrentUser | null | undefined): boolean =>
  hasPermission(user, 'workpackage.manage') &&
  (hasPermission(user, 'system.admin') || user?.partyType === 'platform');

/**
 * Resolve a localized blob (`name_i18n` / `description_i18n`) for the active UI
 * language, falling back to the default (ja) and then any present value. The
 * backend stores per-language text; the UI never coerces it.
 */
export const resolveI18nText = (blob: I18nText | null | undefined, lang: string): string => {
  if (!blob) return '';
  const key = (lang as EfsLang) in blob ? (lang as keyof I18nText) : undefined;
  return (key ? blob[key] : undefined) ?? blob[DEFAULT_LANG] ?? blob.en ?? blob['zh-TW'] ?? '';
};

/** The three "affects_*" flags as a stable, iterable list. */
export const AFFECTS_FLAGS = ['quotation', 'report', 'settlement'] as const;
export type AffectsFlag = (typeof AFFECTS_FLAGS)[number];

export const affectsFor = (pkg: WorkPackageDto, flag: AffectsFlag): boolean => {
  if (flag === 'quotation') return pkg.affects_quotation;
  if (flag === 'report') return pkg.affects_report;
  return pkg.affects_settlement;
};
